import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  CreativeForecastJobResult,
  useForecastCreativeMutation,
} from "~/ui-rtk/api/commentModerationApi"
import Button from "~/ui-rtk/components/ui/controls/Button"
import { isAbortError, useWaitForJob } from "~/ui-rtk/hooks/useWaitForJob"
import CreativeForecastDialog from "./CreativeForecastDialog"
import {
  ScoreBadge,
  StanceBar,
  aspectLabel,
} from "~/ui-rtk/pages/CommentModeration/reactions"

const CONFIDENCE_CLS = {
  low: "bg-system-gray-500/20 text-system-gray-300",
  medium: "bg-sky-500/20 text-sky-300",
  high: "bg-emerald-500/20 text-emerald-300",
} as const

function pct(share: number): string {
  return `${Math.round(share * 100)}%`
}

/**
 * "How will the comments go?" — inside the AdForge form, for the asset
 * currently on the ad. Forecasts from the brand's similar past creatives and
 * what their audiences said (MD-5631). An asset that was never analysed is
 * analysed automatically (the same job uploads queue), then forecast; the
 * panel stays quiet until there is something to say.
 */
export default function CreativeForecastPanel({
  assetId,
  onAnalysisAvailable,
}: {
  assetId: string
  /** Called once a forecast finished, i.e. the asset now carries a saved analysis (and its tag picks). */
  onAnalysisAvailable?: () => void
}) {
  const [forecastCreative] = useForecastCreativeMutation()
  const waitForJob = useWaitForJob()
  const [forecast, setForecast] = useState<CreativeForecastJobResult | null>(
    null,
  )
  const [phase, setPhase] = useState<"idle" | "running">("idle")
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const onAnalysisAvailableRef = useRef(onAnalysisAvailable)
  onAnalysisAvailableRef.current = onAnalysisAvailable

  // The forecast is a job (it may analyse the asset first and ask Gemini
  // for a read; both can outlast a request): enqueue, then poll the run.
  // The job dedupes on the asset id, so re-opening joins a run in flight.
  const run = useCallback(
    async (id: string, signal: AbortSignal) => {
      setPhase("running")
      setError(null)
      try {
        const { jobRunId } = await forecastCreative({ assetId: id }).unwrap()
        const job = await waitForJob(jobRunId, { signal })
        if (signal.aborted) return
        if (job.status !== "COMPLETED") {
          setError(job.error || "The audience forecast did not finish.")
          return
        }
        const result = job.result as unknown as CreativeForecastJobResult
        setForecast(result)
        if (result.status !== "needs_analysis") {
          onAnalysisAvailableRef.current?.()
        }
      } catch (err) {
        if (isAbortError(err) || signal.aborted) return
        const message = (err as { data?: { message?: string } })?.data?.message
        setError(message || "Could not load the audience forecast.")
      } finally {
        if (!signal.aborted) setPhase("idle")
      }
    },
    [forecastCreative, waitForJob],
  )

  useEffect(() => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setForecast(null)
    void run(assetId, controller.signal)
    return () => controller.abort()
  }, [assetId, run])

  const expectedCounts = useMemo(() => {
    if (!forecast || forecast.status !== "ready") return null
    // The bar wants counts; the forecast carries shares — scale to 100.
    return {
      positive: Math.round(forecast.expected.positive * 100),
      negative: Math.round(forecast.expected.negative * 100),
      question: Math.round(forecast.expected.question * 100),
      neutral: Math.round(forecast.expected.neutral * 100),
    }
  }, [forecast])

  let body: React.ReactNode
  let status: string
  if (phase === "running" && !forecast) {
    status = "working…"
    body = (
      <p className="text-xs text-system-gray-400">
        Reading this creative if it is new to us, then comparing it with the
        creatives you already ran and what their audiences said. A new video
        takes about a minute.
      </p>
    )
  } else if (error) {
    status = "unavailable"
    body = <p className="text-xs text-rose-300">{error}</p>
  } else if (!forecast || forecast.status === "needs_analysis") {
    status = "not analysed"
    body = null
  } else if (forecast.status === "no_history") {
    status = "no history yet"
    body = (
      <p className="text-xs text-system-gray-400">
        {forecast.similarFound === 0
          ? "Nothing to compare with yet: analyse a few of the brand's past ads (Paid Media → Ads → Analyze) and this fills in."
          : `${forecast.similarFound} similar creative${forecast.similarFound === 1 ? "" : "s"} found, but none ran with judged comments yet — comment moderation builds that history.`}
      </p>
    )
  } else {
    status = forecast.ai
      ? `${forecast.ai.confidence} confidence`
      : `${forecast.similarWithComments} similar creative${forecast.similarWithComments === 1 ? "" : "s"}`
    const hasWatchOuts = (forecast.ai?.watchOuts.length ?? 0) > 0
    const hasReplies = (forecast.ai?.prepareReplies.length ?? 0) > 0
    body = (
      <div className="flex flex-col gap-system-sm">
        {forecast.ai && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] ${CONFIDENCE_CLS[forecast.ai.confidence]}`}
              >
                {forecast.ai.confidence} confidence
              </span>
              <span className="text-[11px] text-system-gray-500">
                from {forecast.similarWithComments} similar creative
                {forecast.similarWithComments === 1 ? "" : "s"} ·{" "}
                {forecast.commentsConsidered} comments
              </span>
            </div>
            <p className="text-sm text-system-gray-100">
              {forecast.ai.headline}
            </p>
          </div>
        )}
        {expectedCounts && (
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <StanceBar
                counts={expectedCounts}
                title={`Expected: ${pct(forecast.expected.positive)} positive · ${pct(forecast.expected.question)} questions · ${pct(forecast.expected.neutral)} neutral · ${pct(forecast.expected.negative)} negative`}
              />
            </div>
            <ScoreBadge score={forecast.expectedScore} />
          </div>
        )}
        <div className="grid grid-cols-1 gap-system-sm md:grid-cols-3">
          {hasWatchOuts && forecast.ai && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Watch out for
              </div>
              <ul className="mt-1 list-inside list-disc text-xs text-system-gray-200">
                {forecast.ai.watchOuts.map(w => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {!hasWatchOuts && forecast.objections.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Likely pushback
              </div>
              <ul className="mt-1 flex flex-col gap-0.5 text-xs text-system-gray-200">
                {forecast.objections.slice(0, 4).map(o => (
                  <li key={o.claim}>
                    <span className="font-mono text-system-gray-500">
                      {o.mentions}×
                    </span>{" "}
                    {o.claim}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {forecast.questions.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Questions to expect
              </div>
              <ul className="mt-1 list-inside list-disc text-xs text-system-gray-200">
                {forecast.questions.slice(0, 4).map(q => (
                  <li key={q.question}>{q.question}</li>
                ))}
              </ul>
            </div>
          )}
          {hasReplies && forecast.ai && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-system-gray-500">
                Have ready
              </div>
              <ul className="mt-1 flex flex-col gap-1 text-xs">
                {forecast.ai.prepareReplies.map(r => (
                  <li key={r.to}>
                    <div className="text-system-gray-500">To: {r.to}</div>
                    <div className="text-system-gray-100">{r.reply}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!hasReplies && forecast.aspects.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-system-gray-500">
                What people will talk about
              </div>
              <p className="mt-1 text-xs text-system-gray-200">
                {forecast.aspects
                  .slice(0, 5)
                  .map(a => aspectLabel(a.aspect))
                  .join(", ")}
              </p>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] text-system-gray-500">
            {forecast.evidence
              .slice(0, 3)
              .map(e => `${Math.round(e.similarity * 100)}%`)
              .join(" · ")}{" "}
            similar · {forecast.similarWithComments} creative
            {forecast.similarWithComments === 1 ? "" : "s"} with comments
            {forecast.verified
              ? ` · AI-verified${forecast.rejectedSimilar ? `, ${forecast.rejectedSimilar} set aside` : ""}`
              : ""}
          </span>
          <Button
            size="xs"
            variant={{ variant: "outlined" }}
            onClick={() => setDialogOpen(true)}
          >
            Open full report
          </Button>
        </div>
        {dialogOpen && (
          <CreativeForecastDialog
            forecast={forecast}
            isOpen={dialogOpen}
            onClose={() => setDialogOpen(false)}
          />
        )}
      </div>
    )
  }

  if (!body && phase === "idle") return null

  return (
    <section className="rounded-lg border border-system-blue-600/40 bg-system-blue-900/40 px-system-sm py-system-2xs">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={open}
      >
        <span className="text-xs font-medium text-system-gray-200">
          Audience forecast
        </span>
        <span className="text-[11px] text-system-gray-500">{status}</span>
      </button>
      {open && body && <div className="pt-system-2xs">{body}</div>}
    </section>
  )
}
